import React, { useState } from 'react';
import { EditableField } from '../ui/EditableField';
import { PhoneField, phoneError } from '../ui/PhoneField';
import { getProvinces } from '../../constants/jurisdictions';
import { SupportProgramPicker } from './SupportProgramPicker';
import type { AvecFormValues } from './avecProfile';

interface AvecIdentificationSectionProps {
  values: AvecFormValues;
  isEditing: boolean;
  onChange: (patch: Partial<AvecFormValues>) => void;
  /** Email vérifié du compte connecté, proposé comme contact par défaut. */
  accountEmail?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Formulaire réduit d'une AVEC : pas de forme juridique OHADA, d'actionnariat
 * ni d'incubation. Les membres sont suivis dans l'app Wanzo AVEC.
 */
export const AvecIdentificationSection: React.FC<AvecIdentificationSectionProps> = ({
  values,
  isEditing,
  onChange,
  accountEmail = '',
}) => {
  const [editingEmail, setEditingEmail] = useState(false);
  const email = (values.email || '').trim();
  const fromAccount = !!email && email.toLowerCase() === accountEmail.toLowerCase();
  const set = (field: keyof AvecFormValues) => (value: string | number) => {
    onChange({ [field]: value } as Partial<AvecFormValues>);
    return Promise.resolve();
  };

  return (
    <div className="space-y-8">
      <section className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">1. Le groupe</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <EditableField
            label="Nom du groupe *"
            value={values.raisonSociale}
            onSave={set('raisonSociale')}
            type="text"
            required
            disabled={!isEditing}
          />
          <EditableField
            label="Date de création"
            value={values.creationDate}
            onSave={set('creationDate')}
            type="date"
            disabled={!isEditing}
          />
          <EditableField
            label="Numéro d'enregistrement (facultatif)"
            value={values.registrationNumber}
            onSave={set('registrationNumber')}
            type="text"
            placeholder="Attestation de la chefferie ou du territoire"
            disabled={!isEditing}
          />
          <EditableField
            label="Nombre de membres"
            value={values.memberCount}
            onSave={set('memberCount')}
            type="number"
            placeholder="Par exemple 25"
            disabled={!isEditing}
            validation={(v) => {
              const text = String(v ?? '').trim();
              if (text === '') return null;
              const n = Number(text);
              return Number.isInteger(n) && n > 0 ? null : 'Indiquez un nombre entier de membres.';
            }}
          />
          <PhoneField
            label="Téléphone du président ou de la présidente"
            value={values.presidentPhone}
            onChange={(v) => onChange({ presidentPhone: v })}
            disabled={!isEditing}
            error={isEditing ? phoneError(values.presidentPhone) : null}
            hint="Avec l'indicatif du pays. C'est le numéro qui joint le groupe dans les applications Wanzo et sa structure d'appui."
          />
          <div className="min-w-0 space-y-1">
            {editingEmail && isEditing ? (
              <EditableField
                label="Email de contact (facultatif)"
                value={email}
                onSave={(v) => {
                  onChange({ email: String(v ?? '').trim() });
                  setEditingEmail(false);
                  return Promise.resolve();
                }}
                type="email"
                placeholder="Ex. contact@mon-avec.org"
                validation={(v) => {
                  const text = String(v ?? '').trim();
                  return !text || EMAIL_RE.test(text) ? null : 'Adresse email invalide.';
                }}
              />
            ) : (
              <>
                <span className="block text-sm font-medium text-gray-700">Email de contact</span>
                <div className="flex min-h-[2.75rem] sm:min-h-[2.5rem] flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1">
                  <span className={`min-w-0 flex-1 break-all text-sm ${email ? 'text-gray-900' : 'text-gray-500'}`}>
                    {email || 'Non renseigné'}
                  </span>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => setEditingEmail(true)}
                      className="min-h-[2.75rem] sm:min-h-0 px-1 text-sm font-medium text-[#197ca8] hover:underline"
                    >
                      Modifier
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {fromAccount
                    ? 'Email de votre compte, repris par défaut. Facultatif, modifiable.'
                    : "Facultatif. L'adresse qui joint le groupe et sa structure d'appui."}
                </p>
              </>
            )}
          </div>
          <SupportProgramPicker
            institutionId={values.supportProgramInstitutionId}
            name={values.supportProgramName}
            province={values.province}
            disabled={!isEditing}
            onChange={(next) => onChange(next)}
          />
        </div>
      </section>

      <section className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">2. Lieu</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <EditableField
            label="Province"
            value={values.province}
            onSave={set('province')}
            type="select"
            options={getProvinces().map((p) => ({ value: p.value, label: p.label }))}
            disabled={!isEditing}
          />
          <EditableField
            label="Ville ou territoire"
            value={values.territoire}
            onSave={set('territoire')}
            type="text"
            disabled={!isEditing}
          />
          <EditableField
            label="Commune, chefferie ou secteur"
            value={values.chefferie}
            onSave={set('chefferie')}
            type="text"
            disabled={!isEditing}
          />
          <EditableField
            label="Quartier ou village"
            value={values.village}
            onSave={set('village')}
            type="text"
            disabled={!isEditing}
          />
        </div>
      </section>
    </div>
  );
};

export default AvecIdentificationSection;
